
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSupabaseAuth } from './index';
import { toast } from '@/components/ui/sonner';

export interface StaffAnalytics {
  total_users: number;
  active_assignments: number;
  completed_assignments: number;
  in_progress_assignments: number;
  not_started_assignments: number;
  completion_rate: number;
  total_certificates: number;
  pending_approvals: number;
  generated_at: string;
  staff_level_access: boolean;
}

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  is_approved: boolean;
  created_at: string;
  avatar_url?: string;
  role: string;
}

export interface Certificate {
  id: string;
  user_id: string;
  user_name: string;
  course_id: string;
  course_title: string;
  certificate_number: string;
  issue_date: string;
  verification_hash: string;
  is_verified: boolean;
  metadata: any;
}

interface StaffResponse {
  success?: boolean;
  error?: string;
  message?: string;
  errors?: string[];
  success_count?: number;
  error_count?: number;
}

export const useStaffCapabilities = () => {
  const { hasAccess, user } = useSupabaseAuth();
  const [loading, setLoading] = useState(false);
  const [analytics, setAnalytics] = useState<StaffAnalytics | null>(null);

  const hasStaffAccess = hasAccess('staff');

  // Get staff analytics with real assignment breakdown
  const getStaffAnalytics = async (): Promise<StaffAnalytics | null> => {
    if (!hasStaffAccess) return null;
    
    try {
      setLoading(true);
      
      // Get base analytics from RPC
      const { data, error } = await supabase.rpc('get_staff_user_analytics');
      
      if (error) throw error;
      
      const response = data as unknown as StaffResponse & StaffAnalytics;
      
      if (response?.error) {
        console.error('Staff analytics error:', response.error);
        return null;
      }
      
      // Get real assignment status breakdown from database
      const { data: assignments } = await supabase
        .from('course_assignments')
        .select('status, final_grade, modules_completed, progress_percentage');
      
      let completed = 0;
      let inProgress = 0;
      let notStarted = 0;
      
      assignments?.forEach(a => {
        if (a.status === 'completed') {
          completed++;
        } else if (a.progress_percentage && a.progress_percentage > 0) {
          inProgress++;
        } else if (a.modules_completed === 0 || a.modules_completed === null) {
          notStarted++;
        } else {
          inProgress++;
        }
      });
      
      return {
        ...response,
        completed_assignments: completed,
        in_progress_assignments: inProgress,
        not_started_assignments: notStarted,
        active_assignments: completed + inProgress + notStarted
      } as StaffAnalytics;
    } catch (error) {
      console.error('Error fetching staff analytics:', error);
      toast.error('Failed to load analytics');
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Create user function
  const createUser = async (userData: {
    email: string;
    name: string;
    role: string;
  }): Promise<boolean> => {
    if (!hasStaffAccess) {
      toast.error('Unauthorized: Staff access required');
      return false;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('staff_create_user_safe', {
        user_email: userData.email,
        user_name: userData.name,
        user_role: userData.role
      });

      if (error) throw error;

      const response = data as unknown as StaffResponse;

      if (response?.success) {
        toast.success(response.message || 'User created successfully');
        return true;
      } else {
        toast.error(response?.error || 'Failed to create user');
        return false;
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update user function
  const updateUser = async (userId: string, updates: {
    name?: string;
    email?: string;
    is_approved?: boolean;
  }): Promise<boolean> => {
    if (!hasStaffAccess) {
      toast.error('Unauthorized: Staff access required');
      return false;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('staff_update_user_safe', {
        target_user_id: userId,
        user_name: updates.name || null,
        user_email: updates.email || null,
        is_approved: updates.is_approved !== undefined ? updates.is_approved : null
      });

      if (error) throw error;

      const response = data as unknown as StaffResponse;

      if (response?.success) {
        toast.success(response.message || 'User updated successfully');
        return true;
      } else {
        toast.error(response?.error || 'Failed to update user');
        return false;
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Bulk assign courses function
  const bulkAssignCourses = async (userIds: string[], courseIds: string[]): Promise<boolean> => {
    if (!hasStaffAccess) {
      toast.error('Unauthorized: Staff access required');
      return false;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('staff_bulk_assign_courses_enhanced', {
        p_user_ids: userIds,
        p_course_ids: courseIds,
        p_assigned_by: user?.id
      });

      if (error) throw error;

      const response = data as unknown as StaffResponse;

      if (response?.success) {
        toast.success(response.message || 'Courses assigned successfully');
        if (response.errors && response.errors.length > 0) {
          console.warn('Some assignments failed:', response.errors);
        }
        return true;
      } else {
        toast.error(response?.error || 'Failed to assign courses');
        return false;
      }
    } catch (error) {
      console.error('Error bulk assigning courses:', error);
      toast.error('Failed to assign courses');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Get all users (staff can see all non-admin users)
  const getAllUsers = async (): Promise<StaffUser[]> => {
    if (!hasStaffAccess) return [];

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_staff_managed_users_enhanced');
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Get staff-managed certificates
  const getStaffCertificates = async (): Promise<Certificate[]> => {
    if (!hasStaffAccess) return [];

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('get_staff_certificates');
      
      if (error) throw error;
      
      return data || [];
    } catch (error) {
      console.error('Error fetching certificates:', error);
      toast.error('Failed to load certificates');
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Verify certificate
  const verifyCertificate = async (certificateId: string): Promise<boolean> => {
    if (!hasStaffAccess) {
      toast.error('Unauthorized: Staff access required');
      return false;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase.rpc('staff_verify_certificate', {
        certificate_id: certificateId
      });

      if (error) throw error;

      const response = data as unknown as StaffResponse;

      if (response?.success) {
        toast.success('Certificate verified successfully');
        return true;
      } else {
        toast.error(response?.error || 'Failed to verify certificate');
        return false;
      }
    } catch (error) {
      console.error('Error verifying certificate:', error);
      toast.error('Failed to verify certificate');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Download certificate
  const downloadCertificate = async (certificateId: string): Promise<boolean> => {
    if (!hasStaffAccess) {
      toast.error('Unauthorized: Staff access required');
      return false;
    }

    try {
      setLoading(true);
      
      // Get certificate details
      const { data: certificate } = await supabase
        .from('certificates')
        .select('*')
        .eq('id', certificateId)
        .single();

      if (!certificate) {
        throw new Error('Certificate not found');
      }

      // Generate download by triggering the email function with download intent
      await supabase.functions.invoke('send-certificate-email', {
        body: {
          certificateId: certificate.id,
          userEmail: certificate.user_name, // This would be actual email in real data
          userName: certificate.user_name,
          courseTitle: certificate.course_title,
          certificateNumber: certificate.certificate_number
        }
      });

      toast.success('Certificate download initiated - check your downloads');
      return true;
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Load analytics on mount
  useEffect(() => {
    if (hasStaffAccess) {
      getStaffAnalytics().then(setAnalytics);
    }
  }, [hasStaffAccess]);

  return {
    hasStaffAccess,
    loading,
    analytics,
    getStaffAnalytics,
    createUser,
    updateUser,
    bulkAssignCourses,
    getAllUsers,
    getStaffCertificates,
    verifyCertificate,
    downloadCertificate,
    refreshAnalytics: () => getStaffAnalytics().then(setAnalytics)
  };
};
